'use client'

import React, { useTransition } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import type { VariantProps } from 'class-variance-authority'
import { format as formatTime, subDays } from 'date-fns'
import {
  AudioLines,
  Blocks,
  Bookmark,
  ChefHat,
  ChevronLeft,
  ListTodo,
  ScanBarcode,
} from 'lucide-react'
import { useSession } from 'next-auth/react'
import {
  AddCircle,
  ArrowRightUp,
  ArrowsALogout,
  ArrowToTopRight,
  BagSmile,
  Dollar,
  GraphUp,
  Monitor,
  RefreshSquare,
  Restart,
  SettingsBroken,
  Shop,
} from 'solar-icons'
import { toast } from 'sonner'
import {
  getBottomCredits,
  getLocalLocation,
  setCookieCartId,
  type BottomCredits,
} from '@/lib/actions'
import { useCart, useCheckout, useModals, usePagePath } from '@/lib/hooks'

import {
  Button,
  CmdChip,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LoadingDots,
  Separator,
} from '@/components/ui'
import type { buttonVariants, cmdChipVariants } from '@/components/ui'

import { ProfileDropdown } from './profile-dropdown'

interface MenuItem {
  type: 'link' | 'separator' | 'dropdown'
  label?: string
  path?: string
  isActive?: boolean
  icon?: React.ElementType
  button?: VariantProps<typeof buttonVariants> &
    React.ComponentPropsWithoutRef<'button'>
  cmd?: VariantProps<typeof cmdChipVariants> &
    React.ComponentPropsWithoutRef<'div'>
  action?: () => void
  isLoading?: boolean
  children?: (MenuItem | null)[] | null
}

export const DashboardSidebar = ({ location }: { location: any }) => {
  const router = useRouter()
  const { openModal } = useModals()
  const { pathname } = usePagePath()
  const { startNewSale, resetCheckout } = useCheckout()
  const { resetCart } = useCart()
  const [isStartingNewSale, setIsStartingNewSale] = React.useState(false)

  const { data: session }: any = useSession()

  const toDate = formatTime(new Date(), 'yyyy-MM-dd')
  const fromDate = formatTime(new Date(), 'yyyy-MM-dd')

  const [, setBottomCreditTransition] = useTransition()

  const [bottomCredits, setBottomCredits] = React.useState<BottomCredits>({
    userName: '',
    location: '',
    softwareVersion: '',
    softwareName: 'CloudPOS',
  })

  const getBottomCredit = React.useCallback(async () => {
    setBottomCreditTransition(async () => {
      const credits = await getBottomCredits()
      setBottomCredits(credits)
    })
  }, [])

  React.useEffect(() => {
    getBottomCredit()
  }, [getBottomCredit])

  const handleNewSale = async () => {
    setIsStartingNewSale(true)
    resetCheckout()
    resetCart()

    if (location?.type && location?.type === 'restaurant') {
      openModal('SELECT_NEW_ORDER_OPTIONS')
    } else {
      const cart = await startNewSale()
      if (!cart) {
        toast.error('Failed to start a new sale')
        return
      }
      await setCookieCartId(cart.id)
      router.push(`/sale?cartId=${cart.id}`)
    }
  }

  const menuItems: (MenuItem | null)[] = [
    {
      type: 'link',
      label: 'New Sale',
      icon: AddCircle,
      isActive: pathname === '/sale',
      button: {
        variant: 'brand',
      },
      cmd: {
        variant: 'dark',
        children: 1,
      },
      action: handleNewSale,
      isLoading: isStartingNewSale,
    },
    {
      type: 'link',
      label: 'Return Order',
      icon: Restart,
      action: () => openModal('RETURN_ORDER'),
      button: {
        variant: 'default',
      },
      cmd: {
        variant: 'lighter',
        children: 2,
      },
    },
    {
      type: 'separator',
    },
    {
      type: 'link',
      label: 'Orders',
      icon: ListTodo,
      path: `/orders?from=${fromDate}&to=${toDate}`,
      isActive: pathname === '/orders',
      button: {
        variant: 'outline',
      },
      cmd: {
        children: 3,
      },
    },
    {
      type: 'link',
      label: 'Saved Orders',
      icon: Bookmark,
      path: '/saved-orders',
      isActive: pathname === '/saved-orders',
      button: {
        variant: 'outline',
      },
      cmd: {
        children: 4,
      },
    },
    location?.type === 'restaurant' || location?.type === 'cafe'
      ? {
          type: 'link',
          label: 'Kitchen Orders',
          icon: ChefHat,
          path: '/kot',
          isActive: pathname === '/kot',
          button: {
            variant: 'outline',
          },
          cmd: {
            children: 5,
          },
        }
      : null,
    {
      type: 'separator',
    },
    {
      type: 'dropdown',
      label: 'Reports',
      icon: ArrowToTopRight,
      path: `/stock-report?from=${fromDate}&to=${toDate}&locationId=${
        session?.locationId
      }`,
      isActive: pathname === '/stock-report',
      button: {
        variant: 'outline',
      },
      children: [
        {
          type: 'link',
          label: 'Stock',
          path: `/stock-report?from=${fromDate}&to=${toDate}&locationId=${
            session?.locationId
          }`,
          button: {
            variant: 'outline',
          },
          icon: Blocks,
        },
        {
          type: 'link',
          label: 'Order Items',
          path: `/order-items?from=${fromDate}&to=${toDate}&locationId=${
            session?.locationId
          }`,
          button: {
            variant: 'outline',
          },
          icon: ScanBarcode,
        },
        {
          type: 'link',
          label: 'Order Taxes',
          path: `/order-taxes?from=${fromDate}&to=${toDate}&locationId=${
            session?.locationId
          }`,
          button: {
            variant: 'outline',
          },
          icon: AudioLines,
        },
      ],
    },
  ]

  return (
    <div className="flex h-full w-full flex-col gap-5 py-4">
      <div className="flex w-full flex-grow flex-col gap-1.5">
        {menuItems?.map((item, index) => {
          if (!item) return null

          const { type, path, action } = item

          if (type === 'separator') {
            return (
              <Separator key={index} className="my-3 w-full shrink-[none]" />
            )
          }

          if (type === 'dropdown') {
            return <DropdownButton key={index} {...item} />
          }

          if (action) {
            return <ItemButton key={index} {...item} />
          }

          return (
            <Link
              key={index}
              href={path ?? ''}
              className="inline-flex"
              prefetch={false}
            >
              <ItemButton {...item} />
            </Link>
          )
        })}
      </div>

      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full flex-col gap-1.5">
          <Link href="/order-preview" target="_blank" className="w-full">
            <Button size="lg" variant="outline" className="w-full">
              <div className="flex w-full items-center gap-2">
                <Monitor className="size-5" strokeWidth={1.5} />
                <span className="font-medium">Customer Display</span>
              </div>
            </Button>
          </Link>

          <Button size="lg" variant="outline">
            <div className="flex w-full items-center gap-2">
              <RefreshSquare className="size-5" strokeWidth={1.5} />
              <span className="font-medium">Refresh</span>
            </div>
            <CmdChip>R</CmdChip>
          </Button>

          <Button
            size="lg"
            variant="outline-destructive"
            onClick={() => openModal('CLOSE_REGISTER')}
          >
            <div className="flex w-full items-center gap-2">
              <ArrowsALogout className="size-5" strokeWidth={1.5} />
              <span className="font-medium">Close Register</span>
            </div>
          </Button>

          {bottomCredits?.userName && (
            <ProfileDropdown data={bottomCredits} className="mt-2" />
          )}
        </div>
      </div>
    </div>
  )
}

const ItemButton = ({
  icon: Icon,
  button,
  action,
  label,
  cmd,
  isLoading,
}: any) => {
  return (
    <Button {...button} size="lg" onClick={action} className="w-full">
      {isLoading && <LoadingDots dotClassName="bg-white" />}
      {!isLoading && (
        <>
          <div className="flex w-full items-center gap-2">
            {Icon && <Icon className="size-5" strokeWidth={1.5} />}
            <span className="font-medium">{label}</span>
          </div>
          {cmd?.children && (
            <CmdChip variant={cmd?.variant}>{cmd?.children}</CmdChip>
          )}
        </>
      )}
    </Button>
  )
}

const DropdownButton = ({
  Icon,
  button,
  action,
  label,
  cmd,
  isLoading,
  children,
}: any) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button {...button} size="lg" onClick={action} className="w-full">
          {isLoading && <LoadingDots dotClassName="bg-white" />}
          {!isLoading && (
            <>
              <div className="flex w-full items-center gap-2">
                <ChevronLeft className="size-5" strokeWidth={1.5} />
                {Icon && <Icon className="size-5" strokeWidth={1.5} />}
                <span className="font-medium">{label}</span>
              </div>
              {cmd?.children && (
                <CmdChip variant={cmd?.variant}>{cmd?.children}</CmdChip>
              )}
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={10}
        side="left"
        className="flex w-[220px] flex-col gap-1 bg-gray-50 p-2"
      >
        {children?.length > 0 &&
          children?.map((child: any, index: number) => (
            <a
              title={child?.label}
              href={child?.path ?? ''}
              key={index}
              className="w-full"
            >
              <ItemButton key={index} {...child} />
            </a>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
